<template>
    <div class="flex flex-wrap items-end gap-3">
        <div class="flex flex-col">
            <label :class="LABEL_CLASS" for="team-activity-log-from">
                {{ t("vue.team_activity_log_from") }}
            </label>
            <input
                id="team-activity-log-from"
                v-model="localFrom"
                type="date"
                :class="CONTROL_CLASS"
                @change="applyRange"
            />
        </div>

        <div class="flex flex-col">
            <label :class="LABEL_CLASS" for="team-activity-log-to">
                {{ t("vue.team_activity_log_to") }}
            </label>
            <input
                id="team-activity-log-to"
                v-model="localTo"
                type="date"
                :class="CONTROL_CLASS"
                @change="applyRange"
            />
        </div>

        <div
            v-for="field in fields"
            :key="field.key"
            class="flex min-w-[10rem] flex-col"
        >
            <label :class="LABEL_CLASS" :for="`team-activity-log-${field.key}`">
                {{ field.label }}
            </label>
            <select
                :id="`team-activity-log-${field.key}`"
                :value="filters[field.key] ?? ''"
                :class="CONTROL_CLASS"
                @change="applyFilter(field.key, $event)"
            >
                <option value="">{{ t("vue.team_activity_log_all") }}</option>
                <option
                    v-for="value in field.options"
                    :key="value"
                    :value="value"
                >
                    {{ humanizeValue(value) }}
                </option>
            </select>
        </div>

        <button
            v-if="hasActiveFilter"
            type="button"
            class="h-9 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
            @click="clearFilters"
        >
            {{ t("vue.team_activity_log_clear_filters") }}
        </button>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useTranslations } from "@/composables/useTranslations";
import {
    EMPTY_ACTIVITY_LOG_FILTERS,
    type ActivityLogFilterKey,
    type IActivityLogFilterOptions,
    type IActivityLogFilters,
    type IActivityLogRange,
} from "@/types/ActivityLog/IActivityLogFilters";
import { humanizeValue } from "../humanizeValue";

interface IFilterField {
    key: ActivityLogFilterKey;
    label: string;
    options: string[];
}

const { t } = useTranslations();

const LABEL_CLASS =
    "text-[11px] font-medium uppercase tracking-wide text-gray-400";
const CONTROL_CLASS =
    "mt-1 h-9 rounded-md border border-gray-200 bg-white px-3 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-200";

const props = defineProps<{
    range: IActivityLogRange;
    filters: IActivityLogFilters;
    filterOptions: IActivityLogFilterOptions;
}>();

const emit = defineEmits<{
    (
        event: "change",
        range: IActivityLogRange,
        filters: IActivityLogFilters,
    ): void;
}>();

const localFrom = ref<string>(props.range.from);
const localTo = ref<string>(props.range.to);

watch(
    () => props.range,
    (range: IActivityLogRange): void => {
        localFrom.value = range.from;
        localTo.value = range.to;
    },
    { deep: true },
);

const fields = computed((): IFilterField[] => [
    {
        key: "entity_type",
        label: t("vue.team_activity_log_col_entity"),
        options: props.filterOptions.entity_types,
    },
    {
        key: "event_code",
        label: t("vue.team_activity_log_col_event"),
        options: props.filterOptions.event_codes,
    },
    {
        key: "operation_type",
        label: t("vue.team_activity_log_col_operation"),
        options: props.filterOptions.operation_types,
    },
    {
        key: "source",
        label: t("vue.team_activity_log_col_source"),
        options: props.filterOptions.sources,
    },
]);

const hasActiveFilter = computed((): boolean =>
    fields.value.some((field) => props.filters[field.key] !== null),
);

const applyRange = (): void => {
    if (localFrom.value === "" || localTo.value === "") {
        return;
    }

    if (localFrom.value > localTo.value) {
        return;
    }

    emit("change", { from: localFrom.value, to: localTo.value }, props.filters);
};

const applyFilter = (key: ActivityLogFilterKey, event: Event): void => {
    const value = (event.target as HTMLSelectElement).value;

    emit("change", props.range, {
        ...props.filters,
        [key]: value === "" ? null : value,
    });
};

const clearFilters = (): void => {
    emit("change", props.range, { ...EMPTY_ACTIVITY_LOG_FILTERS });
};
</script>
